import { useLanguage } from "@/context/language"
import type { CoworkFile } from "@/cowork/api"
import { createMemo, ErrorBoundary, For, onCleanup, onMount, Show, Suspense, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { FileExtractedData } from "./file-extracted-data"

type InspectionMode = "document" | "data" | "split"

export function FileInspection(props: {
  file?: CoworkFile
  periodID?: string | null
  initialMode?: InspectionMode
  document: (file: CoworkFile) => JSX.Element
  empty?: JSX.Element
}) {
  const language = useLanguage()
  const [state, setState] = createStore({
    mode: props.initialMode ?? "document",
    wide: false,
    dataVisited: props.initialMode === "split",
  })
  let container: HTMLDivElement | undefined
  onMount(() => {
    if (!container) return
    const observer = new ResizeObserver(([entry]) => setState("wide", entry.contentRect.width >= 900))
    observer.observe(container)
    onCleanup(() => observer.disconnect())
  })
  const mode = createMemo(() => (state.mode === "split" && !state.wide ? "document" : state.mode))
  const modes = createMemo<InspectionMode[]>(() => (state.wide ? ["document", "data", "split"] : ["document", "data"]))
  const label = (value: InspectionMode) =>
    language.t(
      value === "document"
        ? "cowork.accounting.extractions.section.document"
        : value === "data"
          ? "cowork.enterprise.data.extracted"
          : "cowork.files.extraction.split",
    )
  return (
    <div ref={(element) => (container = element)} class="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <Show when={props.file} fallback={props.empty}>
        <div
          class="flex shrink-0 gap-1 border-b border-v2-border-border-base p-2"
          role="group"
          aria-label={language.t("cowork.navigation.files")}
        >
          <For each={modes()}>
            {(value) => (
              <button
                type="button"
                class="rounded-md px-3 py-2 text-[12px] hover:bg-v2-overlay-simple-overlay-hover"
                classList={{ "bg-v2-background-bg-layer-03": mode() === value }}
                aria-pressed={mode() === value}
                onClick={() => setState({ mode: value, dataVisited: state.dataVisited || value !== "document" })}
              >
                {label(value)}
              </button>
            )}
          </For>
        </div>
        <div
          class="grid min-h-0 flex-1"
          classList={{ "grid-cols-2": mode() === "split", "grid-cols-1": mode() !== "split" }}
        >
          <div class="min-h-0 min-w-0 overflow-auto" classList={{ hidden: mode() === "data" }}>
            <Show when={props.file} keyed>
              {(file) => (
                <InspectionBoundary>
                  <div class="h-full">{props.document(file)}</div>
                </InspectionBoundary>
              )}
            </Show>
          </div>
          <div
            class="min-h-0 min-w-0 overflow-auto"
            classList={{ hidden: mode() === "document", "border-l border-v2-border-border-base": mode() === "split" }}
          >
            <Show when={state.dataVisited}>
              <Show when={props.file} keyed>
                {(file) => (
                  <InspectionBoundary>
                    <FileExtractedData
                      file={file}
                      periodID={props.periodID ?? file.period_id ?? file.workflow_id}
                      active={mode() !== "document"}
                    />
                  </InspectionBoundary>
                )}
              </Show>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  )
}

function InspectionBoundary(props: { children: JSX.Element }) {
  const language = useLanguage()
  return (
    <ErrorBoundary
      fallback={(_error, reset) => (
        <div role="alert" class="p-4 text-[12px] text-v2-text-text-muted">
          {language.t("common.requestFailed")}
          <button type="button" class="ml-3 underline" onClick={reset}>
            {language.t("cowork.enterprise.workflows.reporting.retry")}
          </button>
        </div>
      )}
    >
      <Suspense
        fallback={
          <p role="status" class="p-4 text-[12px] text-v2-text-text-muted">
            {language.t("cowork.files.panel.loadingPreview")}
          </p>
        }
      >
        {props.children}
      </Suspense>
    </ErrorBoundary>
  )
}
